import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { Router } from '@angular/router'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { BehaviorSubject, Observable, Subject, Subscription, of } from 'rxjs'
import {
  bufferCount,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  switchMap,
  take,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { BulkAction, BulkActionEvent } from 'src/app/shared/interfaces'
import { memberSelectors } from '../../../member/member.selectors'
import { Product } from '../../../product/product'
import { productSelectors } from '../../../product/product.selectors'
import { Booking } from '../../booking'
import { bookingActions } from '../../booking.actions'
import { bookingSelectors } from '../../booking.selectors'
import {
  SerieProposal,
  SerieProposalStatus,
  SerieProposalType
} from '../serie-proposal'
import {
  SerieProposalItem,
  SerieProposalItemStatus
} from '../serie-proposal-item/serie-proposal-item'
import { SerieProposalItemEditModal } from '../serie-proposal-item/serie-proposal-item-create-edit-modal/show.component'
import { serieProposalItemActions } from '../serie-proposal-item/serie-proposal-item.actions'
import { serieProposalItemSelectors } from '../serie-proposal-item/serie-proposal-item.selectors'
import { SerieProposalItemService } from '../serie-proposal-item/serie-proposal-item.service'
import { serieProposalActions } from '../serie-proposal.actions'
import { serieProposalSelectors } from '../serie-proposal.selectors'
import { SerieProposalService } from '../serie-proposal.service'
import { ConfirmationModalComponent } from './confirmation-modal/confirmation-modal.component'
import {
  DateRangeRepeatConfig,
  DateRangeRepeatType
} from 'src/app/shared/components/date-range-repeat/date-range-repeat'
import { getTargetDatesFromConfig } from 'src/app/shared/components/date-range-repeat/date-range-transformer'
import { getAllDaysNotAvailabilityInYearBuffer } from 'src/app/shared/until'
import { MetaOptionKeys } from 'src/app/features/meta-option/meta-option'
import { persistentUserRoleSelectors } from 'src/app/features/role/user-role/persistent-user-role/persistent-user-role.selector'
import { metaOptionSelectors } from 'src/app/features/meta-option/meta-option.selectors'
import { AccessRights } from 'src/app/features/role/user-role/user-role'

@UntilDestroy()
@Component({
  selector: 'serie-proposal-series-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SerieProposalSeriesTabComponent implements OnInit, OnDestroy {
  @Input() serieProposalId: number
  @Input() fromInitialRequest: boolean = false
  public accessRights = AccessRights
  public proposalStatus = SerieProposalStatus
  public serieProposalItems$: BehaviorSubject<SerieProposalItem[]> =
    new BehaviorSubject<SerieProposalItem[]>([])
  public serieProposal$: Observable<SerieProposal>
  public serieProposalId$: Observable<number>
  public form: UntypedFormGroup
  public bulkAction = BulkAction
  public bsModalRef: BsModalRef
  public searchTerm$ = new Subject<string>()
  public searchTerm = ''
  public total: number = 0
  public page: number = 1
  public limit: number = 10
  public pageCount: number = 0
  public isLoading: boolean = false
  public intersectingBookings: Booking[] = []
  public refreshData$: Subject<boolean> = new Subject<boolean>()
  public isLoadingBookings$: Observable<boolean>
  public initBookingsLoading = true
  public isLoadingSerieProposalItems$: Observable<boolean>

  private serieProposal: SerieProposal
  private allSerieProposalItems: SerieProposalItem[] = []
  private subscriptions: Subscription[] = []
  private itemsWithConflictingBookings: SerieProposalItem[]
  private productEntities: Dictionary<Product>
  private disabledDates: Date[] = []

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private router: Router,
    private serieProposalService: SerieProposalService,
    private modalService: BsModalService,
    private serieProposalItemService: SerieProposalItemService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.isLoadingBookings$ = this.store
      .select(bookingSelectors.selectIsLoading)
      .pipe(take(3))

    this.isLoadingSerieProposalItems$ = this.store.select(
      serieProposalItemSelectors.selectIsLoading
    )

    this.serieProposalId$ = this.serieProposalId
      ? of(this.serieProposalId)
      : this.store
          .select(serieProposalSelectors.selectIdRouteParam)
          .pipe(filter((id) => Boolean(id)))

    this.serieProposal$ = this.serieProposalId$.pipe(
      switchMap((serieProposalId) =>
        this.store.select(serieProposalSelectors.selectById, serieProposalId)
      ),
      tap((serieProposal) => (this.serieProposal = serieProposal))
    )

    if (this.fromInitialRequest) {
      this.store
        .select(metaOptionSelectors.selectAll)
        .pipe(
          withLatestFrom(
            this.store.select(
              persistentUserRoleSelectors.selectSelectedUserRole
            )
          ),
          untilDestroyed(this)
        )
        .subscribe(([allMetaOptions, userRole]) => {
          const metaOption = allMetaOptions.find(
            (option) =>
              option.key == MetaOptionKeys.DEFAULT_YEAR_MATRIX &&
              option.siteId == userRole?.siteId
          )

          this.disabledDates = []
          this.disabledDates = getAllDaysNotAvailabilityInYearBuffer(
            metaOption?.value ?? null
          )
        })

      this.getInitialSerieProposalItems()
    } else {
      this.fetchSerieProposalItems()
    }

    this.store
      .select(productSelectors.selectEntities)
      .pipe(untilDestroyed(this))
      .subscribe((productEntities) => (this.productEntities = productEntities))

    this.serieProposalId$.pipe(
      switchMap((serieProposalId) =>
        this.store.select(serieProposalItemSelectors.selectByFilter, {
          field: 'serieProposalId',
          value: serieProposalId
        })
      ),
      filter((items) => Boolean(items?.length)),
      distinctUntilChanged((a, b) => JSON.stringify(a) == JSON.stringify(b))
    )

    this.searchTerm$
      .pipe(distinctUntilChanged(), debounceTime(5), untilDestroyed(this))
      .subscribe((term) => {
        this.searchTerm = term
        this.page = 1
        this.fetchSerieProposalItems()
      })

    this.actions$
      .pipe(
        ofType(bookingActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (action) {
          this.intersectingBookings = action.entities
        }
      })

    this.store
      .select(bookingSelectors.selectAll)
      .pipe(untilDestroyed(this))
      .subscribe((allBookings) => {
        const productIds: number[] = [
          ...new Set(
            this.allSerieProposalItems
              .map((item) => item.productId)
              .filter(Boolean)
          )
        ]

        let dependantProductIds: number[] = Array.from(
          new Set(
            this.allSerieProposalItems
              .map(
                (item) =>
                  this.productEntities[item.productId]?.dependantProducts ?? []
              )
              .flatMap((ids) => ids)
          )
        )

        const allProductIds = [...productIds, ...dependantProductIds]

        const firstSerieProposalItem = this.allSerieProposalItems.sort(
          (a, b) =>
            moment(a.startDate).clone().valueOf() <
            moment(b.startDate).clone().valueOf()
              ? -1
              : 1
        )?.[0]

        const lastSerieProposalItem = this.allSerieProposalItems.sort((a, b) =>
          moment(b.startDate).clone().valueOf() <
          moment(a.startDate).clone().valueOf()
            ? -1
            : 1
        )?.[0]

        this.intersectingBookings = allBookings.filter(
          (booking) =>
            allProductIds.includes(booking.productId) &&
            moment(booking.startDate).isBefore(
              moment(lastSerieProposalItem.endDate)
            ) &&
            moment(booking.endDate).isAfter(
              moment(firstSerieProposalItem.startDate)
            )
        )
      })
  }

  public getInitialSerieProposalItems() {
    this.serieProposal$.pipe(take(1)).subscribe((serieProposal) => {
      if (serieProposal.type == SerieProposalType.REPEATABLE_BOOKING) {
        this.serieProposalItems$.next(
          this.getSerieProposalItems().sort((a, b) =>
            moment(a.startDate).isBefore(b.startDate) ? -1 : 1
          )
        )
      } else {
        const serieProposalItems =
          serieProposal.details.initialRequest?.customBookings.map(
            (booking) => ({
              startDate: moment(booking.startDate),
              endDate: moment(booking.endDate),
              productId: serieProposal.details.initialRequest.productId,
              linkedProductId:
                serieProposal.details.initialRequest.linkedProductId,
              status: SerieProposalItemStatus.REQUESTED
            })
          )

        this.serieProposalItems$.next(serieProposalItems)
      }
    })
  }

  public onBulkAction(bulkActionEvent: BulkActionEvent) {
    if (bulkActionEvent.action == BulkAction.DECLINE) {
      bulkActionEvent.items.map((item) => {
        this.store.dispatch(
          serieProposalItemActions.updateOneById({
            id: item.id,
            params: {
              id: item.id,
              status: SerieProposalItemStatus.DECLINED
            }
          })
        )
      })
      this.actions$
        .pipe(
          ofType(serieProposalItemActions.upsertMany),
          bufferCount(bulkActionEvent.items?.length),
          timeout(5000),
          take(1),
          catchError((error) => {
            this.refreshData$.next(true)
            this.isLoading = false
            this.store.dispatch(
              serieProposalItemActions.setLoading({ isLoading: false })
            )
            return of(null)
          })
        )
        .subscribe((action) => {
          this.store.dispatch(
            serieProposalItemActions.setLoading({ isLoading: false })
          )
          this.fetchSerieProposalItems()
        })
    }
    if (bulkActionEvent.action == BulkAction.ACCEPT) {
      this.onAcceptItems(bulkActionEvent.items)
      this.actions$
        .pipe(
          ofType(serieProposalItemActions.upsertMany),
          bufferCount(bulkActionEvent.items?.length),
          timeout(5000),
          take(1),
          catchError((error) => {
            this.refreshData$.next(true)
            this.isLoading = false
            this.store.dispatch(
              serieProposalItemActions.setLoading({ isLoading: false })
            )
            return of(null)
          })
        )
        .subscribe((action) => {
          this.store.dispatch(
            serieProposalItemActions.setLoading({ isLoading: false })
          )
          this.fetchSerieProposalItems()
        })
    }
  }

  public onClickRow(serieProposalItem: SerieProposalItem): void {
    this.store
      .select(memberSelectors.selectEntities)
      .pipe(take(1))
      .subscribe((memberEntities) => {
        const modalRef = this.modalService.show(SerieProposalItemEditModal, {
          initialState: {
            serieProposalItem,
            member: memberEntities[this.serieProposal.customerPersonId]
          },
          class: 'modal-xl modal-dialog-scrollable'
        })
        modalRef.onHide
          .pipe(take(1), untilDestroyed(this))
          .subscribe((event) => {
            this.fetchSerieProposalItems()
          })
      })
  }

  public onSerieProposalItemClickInSchedule(
    bookableSlotWithSerieProposalItemId
  ) {
    this.store
      .select(serieProposalItemSelectors.selectEntities)
      .pipe(
        withLatestFrom(this.store.select(memberSelectors.selectEntities)),
        take(1)
      )
      .subscribe(([serieProposalItemEntities, memberEntities]) => {
        const modalRef = this.modalService.show(SerieProposalItemEditModal, {
          initialState: {
            serieProposalItem:
              serieProposalItemEntities[
                bookableSlotWithSerieProposalItemId.serieProposalItemId
              ],
            member: memberEntities[this.serieProposal.customerPersonId]
          },
          class: 'modal-xl modal-dialog-scrollable'
        })
        modalRef.onHide
          .pipe(take(1), untilDestroyed(this))
          .subscribe((event) => {
            this.fetchSerieProposalItems()
          })
      })
  }

  private fetchSerieProposalItems() {
    this.serieProposalItems$.next([])

    this.serieProposalId$.pipe(take(1)).subscribe((serieProposalId) =>
      this.store.dispatch(
        serieProposalItemActions.fetchMany({
          params: RequestQueryBuilder.create({
            page: this.page,
            limit: this.limit,
            sort: { field: 'startDate', order: 'ASC' },
            search: {
              serieProposalId
            },
            join: [{ field: 'product' }, { field: 'product.dependantProducts' }]
          })
        })
      )
    )

    this.actions$
      .pipe(
        ofType(serieProposalItemActions.upsertMany),
        take(1),
        catchError((error) => of(null))
      )
      .subscribe((action: any) => {
        if (this.itemsWithConflictingBookings?.length) {
          this.notificationService.emit(
            'serieProposalModule.itemsWithConflictingBookingsCannotBeAccepted',
            'warning'
          )
        }

        this.itemsWithConflictingBookings = null
        this.isLoading = false

        if (!action) {
          this.serieProposalItems$.next([])
          return
        }

        this.total = action.total
        this.pageCount = action.pageCount

        this.serieProposalItems$.next(action?.entities)

        this.setSerieProposalStatus()

        this.fetchIntersectingBookings()
      })
  }

  private fetchIntersectingBookings() {
    this.serieProposalItems$
      .pipe(first((items) => Boolean(items?.length)))
      .subscribe((items) => {
        const serieProposalItems = [...items]

        if (!serieProposalItems.length) {
          return
        }

        let productIds: number[] = [
          ...new Set(
            serieProposalItems.map((item) => item.productId).filter(Boolean)
          )
        ]

        let dependantProductIds: number[] = Array.from(
          new Set(
            serieProposalItems
              .map(
                (item) =>
                  this.productEntities[item.productId]?.dependantProducts ?? []
              )
              .flatMap((ids) => ids)
          )
        )

        const allProductIds = [...productIds, ...dependantProductIds]

        const firstSerieProposalItem = serieProposalItems.sort((a, b) =>
          moment(a.startDate).clone().valueOf() <
          moment(b.startDate).clone().valueOf()
            ? -1
            : 1
        )?.[0]

        const lastSerieProposalItem = serieProposalItems.sort((a, b) =>
          moment(b.startDate).clone().valueOf() <
          moment(a.startDate).clone().valueOf()
            ? -1
            : 1
        )?.[0]

        const serieProposalItemBookingIds = serieProposalItems.map(
          (item) => item.bookingId
        )

        const searchConditions: any = {
          $or: [
            {
              startDate: {
                [CondOperator.LOWER_THAN]: moment(
                  lastSerieProposalItem.endDate
                ).format('YYYY-MM-DD HH:mm')
              },
              endDate: {
                [CondOperator.GREATER_THAN]: moment(
                  firstSerieProposalItem.startDate
                ).format('YYYY-MM-DD HH:mm')
              },
              $or: [
                allProductIds?.length
                  ? { productId: { [CondOperator.IN]: allProductIds } }
                  : null,
                allProductIds?.length
                  ? { linkedProductId: { [CondOperator.IN]: allProductIds } }
                  : null
              ].filter((c) => Boolean(c))
            },
            // Fetch bookings of current day
            {
              startDate: {
                [CondOperator.BETWEEN]: [
                  moment().startOf('day').format('YYYY-MM-DD HH:mm'),
                  moment().endOf('day').format('YYYY-MM-DD HH:mm')
                ]
              },
              $or: [
                allProductIds?.length
                  ? { productId: { [CondOperator.IN]: allProductIds } }
                  : null,
                allProductIds?.length
                  ? { linkedProductId: { [CondOperator.IN]: allProductIds } }
                  : null
              ].filter((c) => Boolean(c))
            }
          ]
        }

        if (serieProposalItemBookingIds.length > 0) {
          searchConditions.id = {
            [CondOperator.NOT_IN]: serieProposalItemBookingIds
          }
        }

        this.store.dispatch(
          bookingActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: searchConditions
            })
          })
        )
      })
    if (!this.allSerieProposalItems?.length) {
      return
    }

    let productIds: number[] = [
      ...new Set(
        this.allSerieProposalItems.map((item) => item.productId).filter(Boolean)
      )
    ]

    let dependantProductIds: number[] = Array.from(
      new Set(
        this.allSerieProposalItems
          .map(
            (item) =>
              this.productEntities[item.productId]?.dependantProducts ?? []
          )
          .flatMap((ids) => ids)
      )
    )

    const allProductIds = [...productIds, ...dependantProductIds]

    const firstSerieProposalItem = this.allSerieProposalItems.sort((a, b) =>
      moment(a.startDate).clone().valueOf() <
      moment(b.startDate).clone().valueOf()
        ? -1
        : 1
    )?.[0]

    const lastSerieProposalItem = this.allSerieProposalItems.sort((a, b) =>
      moment(b.startDate).clone().valueOf() <
      moment(a.startDate).clone().valueOf()
        ? -1
        : 1
    )?.[0]

    const serieProposalItemBookingIds = this.allSerieProposalItems.map(
      (item) => item.bookingId
    )

    this.store.dispatch(
      bookingActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            id: { [CondOperator.NOT_IN]: serieProposalItemBookingIds },
            $or: [
              {
                startDate: {
                  [CondOperator.LOWER_THAN]: moment(
                    lastSerieProposalItem.endDate
                  ).format('YYYY-MM-DD HH:mm')
                },
                endDate: {
                  [CondOperator.GREATER_THAN]: moment(
                    firstSerieProposalItem.startDate
                  ).format('YYYY-MM-DD HH:mm')
                },

                $or: [
                  allProductIds?.length
                    ? {
                        productId: {
                          [CondOperator.IN]: allProductIds
                        }
                      }
                    : null,
                  allProductIds?.length
                    ? {
                        linkedProductId: {
                          [CondOperator.IN]: allProductIds
                        }
                      }
                    : null
                ].filter((c) => Boolean(c))
              },
              // fetch bookings of current day
              {
                startDate: {
                  [CondOperator.BETWEEN]: [
                    moment().startOf('day').format('YYYY-MM-DD HH:mm'),
                    moment().endOf('day').format('YYYY-MM-DD HH:mm')
                  ]
                },
                $or: [
                  allProductIds?.length
                    ? {
                        productId: {
                          [CondOperator.IN]: allProductIds
                        }
                      }
                    : null,
                  allProductIds?.length
                    ? {
                        linkedProductId: {
                          [CondOperator.IN]: allProductIds
                        }
                      }
                    : null
                ].filter((c) => Boolean(c))
              }
            ]
          }
        })
      })
    )
  }

  private setSerieProposalStatus() {
    if (!this.allSerieProposalItems?.length || !this.serieProposal) {
      return
    }

    const hasRequestedItem = this.hasRequestedItem()
    const hasAcceptedStatus = this.hasAcceptedStatus()
    const hasCounterProposalStatus = this.hasCounterProposalStatus()
    const hasOnlyDeclinedStatuses = this.hasOnlyDeclinedStatuses()

    if (
      hasOnlyDeclinedStatuses &&
      this.serieProposal.status !== SerieProposalStatus.DECLINED
    ) {
      this.setStatus(SerieProposalStatus.DECLINED)
    } else if (
      hasAcceptedStatus &&
      this.serieProposal.status !== SerieProposalStatus.ACCEPTED
    ) {
      this.setStatus(SerieProposalStatus.ACCEPTED)
    } else if (
      hasCounterProposalStatus &&
      this.serieProposal.status !== SerieProposalStatus.COUNTER_PROPOSAL
    ) {
      this.setStatus(SerieProposalStatus.COUNTER_PROPOSAL)
    } else if (
      hasRequestedItem &&
      !hasCounterProposalStatus &&
      this.serieProposal.status !== SerieProposalStatus.REQUESTED
    ) {
      this.setStatus(SerieProposalStatus.REQUESTED)
    }
  }

  private setStatus(status: SerieProposalStatus) {
    this.store.dispatch(
      serieProposalActions.updateOneById({
        id: this.serieProposal.id,
        params: {
          status
        }
      })
    )
  }

  private hasRequestedItem(): boolean {
    return this.allSerieProposalItems.some(
      (item) => item.status === SerieProposalItemStatus.REQUESTED
    )
  }

  private hasAcceptedStatus(): boolean {
    const statuses = this.getNonDeclinedStatuses()
    return (
      statuses.length > 0 &&
      statuses.every((status) => status === SerieProposalItemStatus.ACCEPTED)
    )
  }

  private hasCounterProposalStatus(): boolean {
    return this.getNonDeclinedStatuses().includes(
      SerieProposalItemStatus.COUNTER_PROPOSAL
    )
  }

  private hasOnlyDeclinedStatuses(): boolean {
    const statuses = this.allSerieProposalItems.map((item) => item.status)
    return (
      statuses.length > 0 &&
      statuses.every((status) => status === SerieProposalItemStatus.DECLINED)
    )
  }

  private getNonDeclinedStatuses(): SerieProposalItemStatus[] {
    return this.allSerieProposalItems
      .filter((item) => item.status !== SerieProposalItemStatus.DECLINED)
      .map((item) => item.status)
  }

  public selectNewPage(page) {
    this.page = page
    this.fetchSerieProposalItems()
  }

  public selectNewLimit(limit) {
    this.limit = limit
    this.page = 1
    this.fetchSerieProposalItems()
  }

  public onSearchTerm(searchTerm) {
    if (searchTerm.length > 0) {
      this.searchTerm$.next(searchTerm)
    } else {
      this.searchTerm = ''
      this.page = 1
    }
  }

  private onAcceptItems(items: SerieProposalItem[]) {
    if (!items?.length) {
      return
    }

    this.fetchIntersectingBookings()

    this.actions$
      .pipe(
        ofType(bookingActions.upsertMany),
        take(1),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (action) {
          this.intersectingBookings = action.entities

          this.acceptSerieProposalItems(items)
        }
      })
  }

  private acceptSerieProposalItems(items: SerieProposalItem[]) {
    this.store
      .select(bookingSelectors.selectAll)
      .pipe(take(1))
      .subscribe((allBookings) => {
        const hasDeclinedItem = items.find(
          (item) => item.status == SerieProposalItemStatus.DECLINED
        )

        this.itemsWithConflictingBookings = items.filter(
          (item) =>
            this.serieProposalItemService.hasConflictingBooking(
              item,
              allBookings
            ) && item.status != SerieProposalItemStatus.ACCEPTED
        )

        if (this.itemsWithConflictingBookings.length == items.length) {
          return this.notificationService.emit(
            'serieProposalModule.itemsWithConflictingBookingsCannotBeAccepted',
            'warning'
          )
        }

        if (!hasDeclinedItem) {
          const serieProposalItemsToAccept = items
            .filter(
              (item) =>
                (item.status == SerieProposalItemStatus.REQUESTED ||
                  item.status == SerieProposalItemStatus.COUNTER_PROPOSAL) &&
                !this.serieProposalItemService.hasConflictingBooking(
                  item,
                  allBookings
                )
            )
            .map((item) => {
              return { id: item.id, status: SerieProposalItemStatus.ACCEPTED }
            })

          if (serieProposalItemsToAccept?.length) {
            this.isLoading = true

            return this.store.dispatch(
              serieProposalItemActions.updateManyByParams({
                updateParams: serieProposalItemsToAccept
              })
            )
          }
        }

        if (hasDeclinedItem) {
          this.bsModalRef = this.modalService.show(ConfirmationModalComponent, {
            initialState: {
              title: 'serieProposalModule.hasDeclinedItemsText',
              buttons: [
                {
                  text: 'serieProposalModule.dontAcceptRejectedItems',
                  class: 'btn btn-soft-primary',
                  accept: false
                },
                {
                  text: 'serieProposalModule.acceptAll',
                  class: 'btn btn-primary',
                  accept: true
                }
              ]
            }
          })

          this.subscriptions.push(
            this.bsModalRef.content.onAccept
              .pipe(take(1))
              .subscribe((acceptDeclinedItems) => {
                const serieProposalItemsToAccept = items
                  .filter((item) => {
                    if (acceptDeclinedItems) {
                      return (
                        (item.status == SerieProposalItemStatus.REQUESTED ||
                          item.status ==
                            SerieProposalItemStatus.COUNTER_PROPOSAL ||
                          item.status == SerieProposalItemStatus.DECLINED) &&
                        !this.serieProposalItemService.hasConflictingBooking(
                          item,
                          allBookings
                        )
                      )
                    }
                    return (
                      (item.status == SerieProposalItemStatus.REQUESTED ||
                        item.status ==
                          SerieProposalItemStatus.COUNTER_PROPOSAL) &&
                      !this.serieProposalItemService.hasConflictingBooking(
                        item,
                        allBookings
                      )
                    )
                  })
                  .map((item) => {
                    return {
                      id: item.id,
                      status: SerieProposalItemStatus.ACCEPTED
                    }
                  })

                if (serieProposalItemsToAccept?.length) {
                  this.isLoading = true

                  return this.store.dispatch(
                    serieProposalItemActions.updateManyByParams({
                      updateParams: serieProposalItemsToAccept
                    })
                  )
                }
              })
          )
        }
      })
  }

  private getSerieProposalItems(): SerieProposalItem[] {
    const serieProposalItems: SerieProposalItem[] = []

    this.serieProposal$.pipe(take(1)).subscribe((serieProposal) => {
      if (!serieProposal.details.initialRequest) {
        return []
      }

      for (let repeatableBooking of serieProposal.details.initialRequest
        .repeatableBookings) {
        const repeatOn = [repeatableBooking.dayOfWeek]

        const [startHour, startMinute] = repeatableBooking.startTime.split(':')
        const [endHour, endMinute] = repeatableBooking.endTime.split(':')

        const fromDate = moment(serieProposal.startDate).clone().set({
          hour: startHour,
          minute: startMinute
        })

        const tillDate = moment(serieProposal.endDate).clone().set({
          hour: endHour,
          minute: endMinute
        })

        let timeStampRepeatOn = {}

        repeatOn.forEach((weekDay) => {
          timeStampRepeatOn[weekDay] = {
            startTime: fromDate.format('HH:mm'),
            endTime: tillDate.format('HH:mm')
          }
        })

        const repeatConfig: DateRangeRepeatConfig = {
          fromDate,
          tillDate,
          repeatType: DateRangeRepeatType.Weekly,
          repeatEvery: serieProposal.details.initialRequest.repeatPattern,
          repeatOn,
          timeStampRepeatOn
        }

        getTargetDatesFromConfig(repeatConfig).map(
          (dateObj: { startDate: moment.Moment; endDate: moment.Moment }) => {
            if (
              serieProposal.type == SerieProposalType.REPEATABLE_BOOKING &&
              serieProposal.details.initialRequest.bookDuringHoliday ===
                false &&
              this.disabledDates?.find(
                (disabledDate) =>
                  moment(disabledDate).format('YYYY-MM-DD') ==
                  moment(dateObj.startDate).format('YYYY-MM-DD')
              )
            ) {
              return
            }

            const endDateWithTime = dateObj.endDate.clone().set({
              hour: endHour,
              minute: endMinute,
              second: 0
            })

            let linkedProductId =
              serieProposal.details.initialRequest.linkedProductId

            if (!linkedProductId) {
              linkedProductId =
                this.productEntities[serieProposal.productId]
                  ?.parentBookableProducts?.[0]
            }

            let productId = serieProposal.details.initialRequest.productId

            if (!productId) {
              productId =
                this.productEntities[serieProposal.productId]
                  ?.parentBookableProducts?.[0]
            }

            serieProposalItems.push({
              linkedProductId: linkedProductId ? +linkedProductId : null,
              productId: productId ? +productId : null,
              startDate: dateObj.startDate.set({ second: 0 }),
              endDate: endDateWithTime,
              status: SerieProposalItemStatus.REQUESTED
            })
          }
        )
      }
    })
    return serieProposalItems
  }

  ngOnDestroy() {
    this.subscriptions.map((s) => s.unsubscribe())
  }
}
